<template>
  <div
    role="status"
    :class="'im-toast im-toast-' + tone"
    @click="click"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <span class="im-toast-bar"></span>

    <span class="im-toast-icon"><component :is="toneIcon" :size="20" :stroke-width="1.75" /></span>

    <div class="im-toast-body">
      <p class="im-toast-title" v-html="title"></p>
      <p v-if="description" class="im-toast-desc" v-html="description"></p>
    </div>

    <div class="im-toast-close-wrap">
      <svg class="im-toast-ring" viewBox="0 0 36 36">
        <!-- Gray track: the full ring, visible wherever the fill hasn't reached yet. -->
        <circle class="track" cx="18" cy="18" :r="RING_R"></circle>
        <!-- Fill: grows clockwise from 12 o'clock as progress goes 0 → 1. -->
        <circle class="fill" cx="18" cy="18" :r="RING_R" :stroke-dasharray="`${RING_C * progress} ${RING_C}`"></circle>
      </svg>
      <button
        type="button"
        class="im-toast-close"
        :aria-label="$t('re_theme.toast.dismiss')"
        @click.stop="close"
      >
        <X :size="14" />
      </button>
    </div>
  </div>
</template>

<script setup>
  // Kit Toast (im- design system §14): dark confirmation card whose close
  // button is ringed by a countdown that FILLS clockwise and auto-dismisses at
  // zero. Hovering pauses the fill exactly where it is — it resumes, never
  // resets — so nothing disappears mid-read.
  import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
  import { CircleCheck, CircleAlert, TriangleAlert, Info, X } from 'lucide-vue-next'

  const emit = defineEmits(['setInterval'])

  const props = defineProps({
    data: Object,
  })

  const RING_R = 15
  const RING_C = 2 * Math.PI * RING_R

  // FlashMessages.js types → kit tones. `warning` keeps its own tone via a
  // local override (kit ships success/alert/neutral only).
  const TONE_BY_TYPE = { success: 'success', error: 'alert', warning: 'warning' }
  const ICON_BY_TONE = { success: CircleCheck, alert: CircleAlert, warning: TriangleAlert, neutral: Info }

  const tone = computed(() => TONE_BY_TYPE[props.data.type] || 'neutral')
  const toneIcon = computed(() => ICON_BY_TONE[tone.value])

  // Server flash arrives as a bare message: promote it to the (bold) title.
  // Callers passing an explicit title keep it, with the message as description.
  const title = computed(() => props.data.title || props.data.msg)
  const description = computed(() => (props.data.title ? props.data.msg : null))

  const progress = ref(0) // 0 (just shown) → 1 (time's up)
  let rafId = null
  let runStart = null
  let elapsed = 0

  const duration = props.data.timeout // 0 / undefined ⇒ indefinite, no countdown

  function tick (now) {
    if (runStart === null) runStart = now
    const total = elapsed + (now - runStart)
    progress.value = Math.min(1, total / duration)
    if (progress.value >= 1) {
      close()
      return
    }
    rafId = requestAnimationFrame(tick)
  }

  function play () {
    if (!duration) return
    runStart = null
    rafId = requestAnimationFrame(tick)
  }

  function pause () {
    if (rafId) cancelAnimationFrame(rafId)
    if (runStart !== null) {
      elapsed += performance.now() - runStart
      runStart = null
    }
  }

  onMounted(play)
  onBeforeUnmount(pause)

  function onMouseEnter () {
    if (props.data.onMouseOver) props.data.onMouseOver()
    pause()
  }

  function onMouseLeave () {
    if (props.data.onMouseOut) props.data.onMouseOut()
    play()
  }

  function click () {
    if (props.data.onClick) props.data.onClick()
    if (props.data.clickClose) close()
  }

  function close () {
    pause()
    emit('setInterval', props.data)
  }
</script>
